import { memo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";

import {
  AddSquare,
  Edit2,
  MinusSquare,
  ShoppingCart,
  Trash,
} from "iconsax-react";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Modal from "@mui/material/Modal";

import { useAppDispatch, useAppSelector } from "@zenith/app/hooks";
import EmptyBasket from "@zenith/assets/icons/components/EmptyBasket";
import { ButtonComponent, UpdateCartComponent } from "@zenith/components";
import DiscardPaymentModalComponent from "@zenith/components/ui/DiscardModalComponent/discard-modal-component/discard-modal-component";
import { usePaymentsContext } from "@zenith/context";
import {
  DECIMAL_DIGITS_AMOUNT_REGEX,
  DEFAULT_CURRENCY_SYMBOL,
  DISCARD_PAYMENT_BODY,
  DISCARD_PAYMENT_PRIMARY_BTN_TEXT,
  DISCARD_PAYMENT_SECONDARY_BTN_TEXT,
  DISCARD_PAYMENT_TITLE,
  NOT_NUMBERS_AND_DOT,
  PRODUCT_PLACEHOLDER_LABEL,
  TRIPLE_DIGITS_AMOUNT_REGEX,
} from "@zenith/data/constants";
import {
  deleteCartItem,
  emptyCart,
  selectCart,
  updateCart,
  updateCartItemQuantity,
} from "@zenith/features/Payments/components/ItemDetailsForm/item-details-form-component/cartSlice";
import { CartItem } from "@zenith/features/Payments/interfaces";
import { DASHBOARD, PAYMENT_OVERVIEW } from "@zenith/routes/paths";
import { formatCurrency } from "@zenith/utils";
import ClearCartPopup from "../../ClearCartPopupComponent/clear-cart-popup/clear-cart-popup";
import ChevronLeft from "@zenith/assets/icons/ChevronLeft";
import { EmptyState } from "../../EmptyStateComponent/empty-state-component/empty-state-component";
import { EmptyCart } from "@zenith/assets/illustrations/EmptyCart";

/**
 * Props for Cart Summary Component.
 * @interface
 * @name CartSummaryProps
 * @kind interface
 */
interface CartSummaryProps {
  /**
   * Opens the modal.
   * @method
   * @name openModal
   * @kind property
   * @memberof CartSummaryProps
   * @returns {void}
   */
  openModal: () => void;
  /**
   * Closes the modal.
   * @method
   * @name closeModal
   * @kind property
   * @memberof CartSummaryProps
   * @returns {void}
   */
  closeModal: () => void;
  /**
   * The clear cart popup prop setter.
   * @method
   * @name setShowClearCartPopup
   * @kind property
   * @memberof CartSummaryProps
   * @param {boolean} bool
   * @returns {void}
   */
  setShowClearCartPopup: (bool: boolean) => void;
  /**
   * If `true`, the clear cart popup should be shown.
   * @property
   * @name showClearCartPopup
   * @kind property
   * @memberof CartSummaryProps
   * @type {boolean}
   */
  showClearCartPopup: boolean;
  /**
   * If `true`, the car is open.
   * @property
   * @name isCartOpen
   * @kind property
   * @memberof CartSummaryProps
   * @type {boolean}
   */
  isCartOpen: boolean;
}

const TWO_CHARACTERS_STRING = 2;

/**
 * Memoized Cart Summary Component.
 * @constant
 * @name CartSummaryComponent
 * @kind variable
 * @type {React.NamedExoticComponent<CartSummaryProps>}
 */
const CartSummaryComponent: React.NamedExoticComponent<CartSummaryProps> = memo(
  /**
   * Cart Summary Component.
   * @function
   * @name CartSummaryComponent
   * @kind function
   * @memberof CartSummaryComponent
   * @param {CartSummaryProps} props Props for Cart Summary Component.
   * @returns {JSX.Element} Cart Summary Component.
   */
  function CartSummaryComponent(props: CartSummaryProps): JSX.Element {
    const {
      isCartOpen,
      showClearCartPopup,
      setShowClearCartPopup,
      openModal,
      closeModal,
    } = props;
    const cart = useAppSelector(selectCart);
    const { paymentsContext, setPaymentsContext } = usePaymentsContext();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [focusedInputIndex, setFocusedInputIndex] = useState<number | null>(
      null
    );
    const [isUpdateCartOpen, setIsUpdateCartOpen] = useState<boolean>(false);
    const [itemToEdit, setItemToEdit] = useState<CartItem | undefined>();
    const [isOpen, setIsOpen] = useState<boolean>(false);

    // #region CartSummary Logic.

    const handleUpdateQuantity = (
      event:
        | React.ChangeEvent<HTMLInputElement>
        | React.KeyboardEvent<HTMLInputElement>,
      index: number
    ) => {
      let value = (event.target as HTMLInputElement).value;

      value = value.replace(/\s/g, "");

      const numericValue = value === "" ? 1 : parseInt(value, 10);

      if (!isNaN(numericValue) && numericValue > 0) {
        dispatch(updateCartItemQuantity({ index, quantity: numericValue }));
      }
    };
    const handleBackButton = () => {
      navigate(DASHBOARD, { replace: true });
    };
    const handleAddQuantity = (index: number) => {
      const currentItem = cart[index];
      dispatch(
        updateCartItemQuantity({ index, quantity: currentItem.quantity + 1 })
      );
    };

    const handleRemoveQuantity = (index: number) => {
      const currentItem = cart[index];
      if (currentItem.quantity > 1) {
        dispatch(
          updateCartItemQuantity({ index, quantity: currentItem.quantity - 1 })
        );
      }
    };

    const handleDeleteCartItem = (id: number) => {
      dispatch(deleteCartItem(id));
    };

    const getTotalItems = () => {
      let totalItems = 0;

      cart.forEach((item: CartItem) => {
        totalItems += Number(item.quantity);
      });

      return totalItems > 99 ? "99+" : totalItems.toString();
    };

    const getTotalAmount = () => {
      let sum = 0;

      cart.forEach((item: CartItem) => {
        const amount = parseFloat(item.amount.replace(NOT_NUMBERS_AND_DOT, ""));
        const quantity = Number(item.quantity);
        const subtotal = amount * quantity;

        sum += subtotal;
      });

      const formattedSum = formatCurrency(sum);

      return formattedSum;
    };

    // #endregion CartSummary Logic.

    // #region UpdateCart Logic.

    const openUpdateModal = (item: CartItem) => {
      setItemToEdit(item);
      setIsUpdateCartOpen(true);
    };

    const closeUpdateModal = () => {
      setIsUpdateCartOpen(false);
    };

    const handleUpdateCartItem = (
      itemToUpdate: CartItem,
      amount: string,
      quantity: number | null,
      description: string
    ) => {
      const updatedQuantity = quantity !== null && quantity > 0 ? quantity : 1;
      const updatedDescription =
        description.trim() !== "" ? description : PRODUCT_PLACEHOLDER_LABEL;

      if (amount !== "" && Number(amount) !== 0 && itemToUpdate) {
        const updatedItem: CartItem = {
          id: itemToUpdate.id,
          amount,
          quantity: updatedQuantity,
          description: updatedDescription,
        };

        const index = cart.findIndex(
          (cartItem: CartItem) => cartItem.id === itemToUpdate.id
        );

        if (index !== -1) {
          dispatch(updateCart({ index, updatedItem }));
        }
      }
    };

    // #endregion UpdateCart Logic.

    // #region ClearCart Logic.

    const handleClearCart = () => {
      cart.length > 0 && setShowClearCartPopup(true);
    };

    const handleClearCartPopupClose = () => {
      setShowClearCartPopup(false);
    };

    const handleClearCartPopupButtonClick = () => {
      setShowClearCartPopup(false);
      dispatch(emptyCart());
    };

    // #endregion ClearCart Logic.

    const openDiscardPaymentModal = () => {
      setIsOpen(true);
    };

    const closeDiscardPaymentModal = () => {
      setIsOpen(false);
    };

    const handleBackClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (cart.length > 0) {
        event.preventDefault();
        openDiscardPaymentModal();
        return;
      }
      setPaymentsContext({
        ...paymentsContext,
        paymentMethod: undefined,
      });
    };

    const handleEmptyCart = () => {
      dispatch(emptyCart());
      navigate(DASHBOARD);
      setPaymentsContext({
        ...paymentsContext,
        paymentMethod: undefined,
      });
      closeDiscardPaymentModal();
    };

    return (
      <div className="flex items-start justify-between">
        <ButtonComponent
          label="Back to Dashboard"
          onClick={handleBackButton}
          id="back-to-dashboard"
          size="large"
          type="tertiary"
          iconStart={<ChevronLeft />}
        />
        <DiscardPaymentModalComponent
          isOpen={isOpen}
          onClose={closeDiscardPaymentModal}
          primaryAction={handleEmptyCart}
          secondaryAction={closeDiscardPaymentModal}
          title={DISCARD_PAYMENT_TITLE}
          bodyText={DISCARD_PAYMENT_BODY}
          primaryActionText={DISCARD_PAYMENT_PRIMARY_BTN_TEXT}
          secondaryActionText={DISCARD_PAYMENT_SECONDARY_BTN_TEXT}
        />

        <div className="relative">
          <div className="absolute">
            <IconButton
              id="open-cart-button"
              onClick={openModal}
              className="bottom-2 mr-6 text-neutral-900 hover:bg-white"
            >
              <ShoppingCart className="h-8 w-8" />
            </IconButton>
          </div>
          <div className="absolute left-6 top-[-0.4rem]">
            <p
              className={` ${
                cart.length === 0
                  ? "hidden"
                  : "flex h-5 w-5 items-center justify-center rounded-full bg-primary-300 text-white"
              } ${
                getTotalItems().length > TWO_CHARACTERS_STRING
                  ? "text-xxs"
                  : "text-xs"
              } `}
            >
              {getTotalItems()}
            </p>
          </div>
          <div className="absolute top-8 left-10 translate-x-[-100%] transform">
            <p
              className={`${
                cart.length === 0
                  ? "hidden"
                  : "whitespace-nowrap text-m font-medium text-primary-300"
              }`}
            >
              {getTotalAmount()}
            </p>
          </div>
          <Modal open={isCartOpen} onClose={closeModal}>
            <div className="fixed top-0 left-0 flex h-full w-full items-center justify-center bg-black bg-opacity-50">
              <div className="h-132 w-120 rounded-2.5xl bg-white py-6 px-8 shadow">
                <div className="grid justify-items-end">
                  <IconButton
                    id="close-cart-button"
                    onClick={closeModal}
                    className="mb-4 text-neutral-900 hover:bg-white"
                    size="small"
                  >
                    <CloseIcon fontSize="large" />
                  </IconButton>
                </div>
                <div className="mb-10 flex justify-between">
                  <h1 className="text-2xl font-medium text-neutral-900">
                    Cart Summary
                  </h1>
                  <div className="relative">
                    <Button
                      variant="text"
                      id="clear-cart-button"
                      className={`text-base font-normal normal-case ${
                        cart.length === 0
                          ? "cursor-not-allowed text-neutral-200"
                          : showClearCartPopup
                          ? "text-neutral-900"
                          : "text-neutral-600 hover:text-neutral-900"
                      } hover:bg-white ${
                        cart.length === 0 ? "" : "hover:underline"
                      } ${showClearCartPopup ? "underline" : ""}`}
                      disableRipple={cart.length === 0}
                      onClick={handleClearCart}
                    >
                      Clear Cart
                    </Button>
                    {showClearCartPopup && (
                      <ClearCartPopup
                        handleClearCartPopupClose={handleClearCartPopupClose}
                        handleClearCartPopupButtonClick={
                          handleClearCartPopupButtonClick
                        }
                      />
                    )}
                  </div>
                </div>

                <div className="h-72 overflow-y-auto">
                  {cart.length === 0 && (
                    <div className="flex h-[100%] flex-col items-center justify-center">
                      <EmptyState
                        title="Your Cart is empty"
                        description="Looks like you don't have any items in your cart."
                        illustration={
                          <div className="flex w-full justify-center">
                            <EmptyCart className="block h-auto w-40 text-primary-300" />
                          </div>
                        }
                      />
                    </div>
                  )}
                  {cart.map((item: CartItem, i: number) => (
                    <div key={i}>
                      <div className="mx-2 mb-3 flex justify-between">
                        <p className="text-base font-normal text-neutral-900">
                          {item.description}
                        </p>
                        <p className="text-base font-medium text-neutral-900">
                          {`${DEFAULT_CURRENCY_SYMBOL} ${item.amount
                            .replace(TRIPLE_DIGITS_AMOUNT_REGEX, "")
                            .replace(DECIMAL_DIGITS_AMOUNT_REGEX, "0$1")}`}
                        </p>
                      </div>
                      <div className="mx-2 mb-4 flex justify-between">
                        <div className="flex items-center">
                          <IconButton
                            id="close-cart-button"
                            onClick={() => handleRemoveQuantity(i)}
                            className=" rounded-sm text-neutral-900 hover:bg-white"
                          >
                            <MinusSquare />
                          </IconButton>
                          <input
                            value={
                              focusedInputIndex === i
                                ? item.quantity === 1
                                  ? ""
                                  : item.quantity.toString()
                                : item.quantity.toString()
                            }
                            placeholder="1"
                            onChange={(event) => handleUpdateQuantity(event, i)}
                            className="w-14 text-center focus:outline-neutral-900"
                            type="text"
                            maxLength={5}
                            onFocus={() => setFocusedInputIndex(i)}
                            onBlur={() => setFocusedInputIndex(null)}
                            onKeyDown={(event) => {
                              const inputElement =
                                event.target as HTMLInputElement; // Cast to HTMLInputElement
                              // Allow typing "1" when the input is empty
                              if (
                                event.key === "1" &&
                                inputElement.value === ""
                              ) {
                                inputElement.value = "1";
                                event.preventDefault();
                                handleUpdateQuantity(event, i);
                              }
                            }}
                          />
                          <IconButton
                            id="close-cart-button"
                            onClick={() => handleAddQuantity(i)}
                            className=" text-neutral-900 hover:bg-white"
                            disabled={cart[i].quantity >= 99999}
                          >
                            <AddSquare />
                          </IconButton>
                        </div>
                        <div className="flex">
                          <IconButton
                            id="update-item-button"
                            onClick={() => openUpdateModal(item)}
                          >
                            <Edit2 className="cursor-pointer text-neutral-900" />
                          </IconButton>

                          <IconButton
                            className="ml-6"
                            onClick={() => handleDeleteCartItem(item.id)}
                          >
                            <Trash className="cursor-pointer text-neutral-900" />
                          </IconButton>
                        </div>
                      </div>
                    </div>
                  ))}

                  <UpdateCartComponent
                    isUpdateCartOpen={isUpdateCartOpen}
                    closeUpdateModal={closeUpdateModal}
                    closeModal={closeModal}
                    item={itemToEdit}
                    handleUpdateCartItem={handleUpdateCartItem}
                  />
                </div>

                <p className="my-3 mt-8 text-m font-medium text-black">
                  Items ({getTotalItems()})
                </p>
                <hr></hr>
                <div className="mt-4 mb-4 flex justify-between pb-5">
                  <p className="text-m font-medium text-black">Total</p>
                  <h3 className="text-main-200-925 text-xl font-medium">
                    {getTotalAmount()}
                  </h3>
                </div>
                <div className="mt-8 flex">
                  <Button
                    id="cancel-cart-button"
                    onClick={closeModal}
                    className="mr-4 h-12 w-full rounded-lg border-1 border-solid border-neutral-300 py-3 px-10 text-base font-normal normal-case text-neutral-600 hover:border-neutral-300 hover:bg-white"
                    size="large"
                  >
                    Add New Items
                  </Button>
                  <Button
                    id="checkout-cart-button"
                    className={`h-12 w-full rounded-lg py-3 px-10 text-base font-medium normal-case  ${
                      cart.length === 0
                        ? "cursor-not-allowed bg-neutral-200 text-neutral-200 hover:bg-neutral-200"
                        : " bg-primary-300 text-white hover:bg-primary-300"
                    }`}
                    size="large"
                    onClick={() => {
                      cart.length > 0 && navigate(PAYMENT_OVERVIEW);
                    }}
                  >
                    Checkout
                  </Button>
                </div>
              </div>
            </div>
          </Modal>
        </div>
      </div>
    );
  }
);

export default CartSummaryComponent;
